import React from "react";
import { Link, useNavigate } from "react-router-dom";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard } from "@/components/ds";

const SKYSWITCH_COLOR = "#1A56DB";

export default function SkySwitch() {
  const [tab, setTab] = React.useState<"overview" | "activity">("overview");

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <img src="/img/skyswitch.svg" alt="SkySwitch" className="w-9 h-9 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h1 className="text-xl font-bold text-zinc-800">SkySwitch</h1>
          <p className="text-xs text-zinc-400">UCaaS / Hosted PBX — CTI Screen Pop + Call Routing</p>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-zinc-200">
        {(["overview", "activity"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              tab === t ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <SectionCard title="SkySwitch UCaaS Integration">
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Connect SkySwitch UCaaS to get a full customer screen pop on every inbound call. Velaro searches your connected CRM, service, and e-commerce integrations in real time and shows matching customer data in the agent sidebar — before they answer. Built for SkySwitch resellers managing multiple sub-tenants.
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">Screen pop</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Multi-tenant</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Call logging</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">IVR integration</span>
            </div>
            <div className="p-4 rounded-xl bg-sky-50 border border-sky-200 text-sm text-sky-900">
              <strong>Setup required.</strong> SkySwitch integration uses the SkySwitch reseller API to receive call events.
              Contact <strong>support@velaro.com</strong> or your account manager. You will need your SkySwitch reseller credentials
              and sub-tenant identifiers to begin.
            </div>
          </SectionCard>

          <SectionCard title="What you get" description="All features use the same screen pop provider system — your CRM integrations are automatically available.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: "📲", title: "Inbound call screen pop", desc: "When a call arrives at any SkySwitch extension, Velaro looks up the caller across all connected integrations and shows customer info in the agent sidebar." },
                { icon: "🏢", title: "Multi-tenant / reseller support", desc: "Each SkySwitch sub-tenant can be configured independently. One Velaro instance can serve multiple businesses under the same reseller account." },
                { icon: "📋", title: "Post-call activity logging", desc: "Completed calls are logged as activities on the matched CRM contact — HubSpot, Salesforce, Dynamics 365, and Velaro CRM." },
                { icon: "🤖", title: "IVR hand-off with context", desc: "Calls handled by Velaro IVR can transfer to SkySwitch extensions with full conversation context — agents see the transcript before connecting." },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-3 rounded-xl border border-zinc-100 bg-zinc-50">
                  <span className="text-xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">{item.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="How screen pop works" description="SkySwitch uses the same automatic lookup system as all Velaro phone integrations.">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Every enabled CRM and service integration on your site is checked automatically when a call arrives — HubSpot, Salesforce, Dynamics 365, ServiceTitan, ServiceNow, VAST, and more. Lookups run in parallel; the first match surfaces in the sidebar.
            </div>
            <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-xs text-zinc-500">
              See the{" "}
              <Link to="/integrations/nextiva" className="text-[var(--color-primary)] underline">
                Nextiva integration page
              </Link>{" "}
              for the full list of integrations with screen pop support and a detailed walkthrough of how the lookup works.
            </div>
          </SectionCard>

          <SectionCard title="Setup process">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: "1", title: "Contact Velaro", desc: "Email support@velaro.com or your account manager. Have your SkySwitch reseller credentials and sub-tenant IDs ready." },
                { step: "2", title: "Reseller API access", desc: "Velaro will request read access to your SkySwitch tenant via the reseller portal to subscribe to call events." },
                { step: "3", title: "Webhook configured", desc: "Velaro generates call event endpoints for each sub-tenant. Screen pop goes live immediately for all extensions." },
              ].map((s) => (
                <div key={s.step} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5" style={{ backgroundColor: SKYSWITCH_COLOR }}>{s.step}</span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">{s.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="SkySwitch" />}
    </div>
  );
}

export function BackToIntegrationLandingSkySwitch() {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate("/integrations")} className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
